import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Lets a page override the breadcrumb's last-segment label client-side.
 *
 * Why this exists: the storefront shell's breadcrumb is computed from
 * `shellData.currentPageTitle`, which is set inside `getServerSideProps` and
 * therefore baked at SSR time. For pages where the meaningful label is only
 * known after a client-side fetch (e.g. the order-detail page, whose URL is
 * an opaque Snipcart token but the right breadcrumb label is the invoice
 * number like `ROCK-2053`), we need a way to swap the label in after the
 * data lands.
 *
 * Usage from a page:
 *   const setTitle = useSetBreadcrumbTitle();
 *   useEffect(() => { if (order) setTitle(order.invoiceNumber); }, [order]);
 *
 * The override is automatically cleared on unmount so the next page's
 * SSR-provided title isn't shadowed by a stale value.
 */

type BreadcrumbTitleContextValue = {
  /** Current override, or null when no page has set one. */
  override: string | null;
  setOverride: (value: string | null) => void;
};

const BreadcrumbTitleContext = createContext<BreadcrumbTitleContextValue | null>(
  null,
);

export function BreadcrumbTitleProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<string | null>(null);
  const value = useMemo(() => ({ override, setOverride }), [override]);
  return (
    <BreadcrumbTitleContext.Provider value={value}>
      {children}
    </BreadcrumbTitleContext.Provider>
  );
}

/** Read the current override (or null). Returns null outside the provider. */
export function useBreadcrumbTitleOverride(): string | null {
  return useContext(BreadcrumbTitleContext)?.override ?? null;
}

/**
 * Returns a setter that updates the override. Pass `null` to clear.
 *
 * Pages don't need to clear manually — pass a value via `useEffect` and the
 * setter returned here is stable across renders. {@link useBreadcrumbTitle}
 * is a thinner wrapper that handles the cleanup for you.
 */
export function useSetBreadcrumbTitle(): (value: string | null) => void {
  const ctx = useContext(BreadcrumbTitleContext);
  return useCallback(
    (value) => {
      ctx?.setOverride(value);
    },
    [ctx],
  );
}

/**
 * Sets the breadcrumb override to `title` for the lifetime of the calling
 * component (and the moment `title` changes), and clears it on unmount.
 * Pass `null` while you don't have a value yet — the override won't
 * be applied until you provide one.
 */
export function useBreadcrumbTitle(title: string | null | undefined): void {
  const setOverride = useSetBreadcrumbTitle();
  useEffect(() => {
    setOverride(title?.trim() || null);
    return () => setOverride(null);
  }, [title, setOverride]);
}
